import type { TVerificationCode, TVerificationInputHandlersProps } from '../../../utils/types';

import { useCallback } from 'react';
import { useTranslation } from '../../../utils';

export function useHandlers({
  inputRefs,
  errorMessage,
  setErrorMessage,
  code,
  dispatch,
  onVerify,
}: TVerificationInputHandlersProps) {
  const { t } = useTranslation();
  const focusOnNext = useCallback(
    (index: number) => {
      if (index < inputRefs.current.length - 1) {
        inputRefs.current[index + 1]?.focus();
        inputRefs.current[index + 1]?.select();
      } else inputRefs.current[index]?.blur();
    },
    [inputRefs],
  );

  const focusOnPrev = useCallback(
    (index: number) => {
      if (index > 0) {
        inputRefs.current[index - 1]?.focus();
        inputRefs.current[index - 1]?.select();
      }
    },
    [inputRefs],
  );

  const hideErrorMessage = useCallback(() => {
    if (!!errorMessage) {
      setErrorMessage(undefined);
    }
  }, [errorMessage, setErrorMessage]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
      hideErrorMessage();
      const value = e.target.value.replace(/[^0-9]/g, '');

      if (value.length <= 1) {
        const fieldName = `codeDigit${index + 1}` as keyof TVerificationCode;
        dispatch({ [fieldName]: value });

        if (value.length === 1 && index <= inputRefs.current.length - 1) {
          focusOnNext(index);
        }
      }
    },
    [dispatch, focusOnNext, hideErrorMessage, inputRefs],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
      if (e.key === 'Backspace') {
        const fieldName = `codeDigit${index + 1}` as keyof TVerificationCode;
        if (code[fieldName]) {
          // Если в текущей ячейке есть значение — стираем его
          dispatch({ [fieldName]: '' });
        } else {
          // Если пустая — переходим на предыдущую и стираем
          const prevFieldName = `codeDigit${index}` as keyof TVerificationCode;
          dispatch({ [prevFieldName]: '' });
          focusOnPrev(index);
        }
      }
    },
    [code, dispatch, focusOnPrev],
  );

  const handleVerify = useCallback(() => {
    hideErrorMessage();
    const codeString = Object.values(code).join('');
    const codeLength = codeString.length;

    switch (codeLength) {
      case 0:
        setErrorMessage(t('Web.Error_verification_code_required'));
        break;
      case 6:
        onVerify(codeString);
        break;
      default:
        setErrorMessage(t('Web.Error_verification_code_length'));
        break;
    }
  }, [code, hideErrorMessage, onVerify, setErrorMessage, t]);

  return { hideErrorMessage, handleInputChange, handleKeyDown, handleVerify };
}